import moment from "moment-timezone";

export const IDP_TRANSFER_AREA_OPTIONS = Object.freeze([
    {id: "HN_MIEN_BAC", name: "HN & Miền Bắc"},
    {id: "DN_HUE_QUANG_TRI", name: "Đà Nẵng, Huế, Quảng Trị"},
    {id: "HCM_MIEN_NAM", name: "HCM & Miền Nam"},
]);

export class IdpExamRegisterModel {
    constructor(row = {}) {
        const user = row.user || {};
        const branch = row.branch || {};
        const payment = row.payment || {};

        this.id = row.id ?? null;
        this.logCode = row.log_code ?? row.code ?? row.id ?? "";

        this.studentId = user.id ?? row.user_id ?? null;
        this.studentCode = row.user_id ? row.user_id + 100000 : '';
        this.studentName = user.name ?? row.user_name ?? row.student_name ?? "";
        this.phone = user.phone ?? row.user_phone ?? row.phone ?? "";

        this.branchName = branch.name ?? row.branch_name ?? row.branchName ?? "";

        this.taskTime = IdpExamRegisterModel.formatDateTime(row.task_time ?? row.created_at ?? "");
        this.examUnit = row.exam_unit ?? row.unit ?? "IDP";

        /** Theo API: payment_amount (số tiền đóng) */
        this.payment_amount = row.payment_amount ?? payment.payment_amount ?? 0;
        /** Theo API: idp_payment_amount */
        this.idp_payment_amount = row.idp_payment_amount ?? payment.idp_payment_amount ?? 0;
        this.paymentMethod = row.payment_method ?? payment.method ?? row.payment_type ?? "";

        // 0: chưa thanh toán, 1: đã thanh toán, 2: hoàn thành, 3: thiếu tiền
        this.status = row.status ?? row.payment_status ?? 0;
        /** Mọi lần có tiền vào (API: payment_dates, Y-m-d H:i:s) */
        this.paymentDates = IdpExamRegisterModel.normalizeStringArray(row.payment_dates);
        /** Ngày thanh toán mới nhất (API: paid_at; fallback: phần tử cuối payment_dates) */
        const paidAtRaw = row.paid_at != null && row.paid_at !== ""
            ? row.paid_at
            : (this.paymentDates.length ? this.paymentDates[this.paymentDates.length - 1] : null);
        this.paidAt = paidAtRaw ? IdpExamRegisterModel.formatDateTime(paidAtRaw) : "";
        /** Mã CK; tiền mặt/quẹt thẻ thường rỗng (API: transaction_codes) */
        this.transactionCodes = IdpExamRegisterModel.normalizeStringArray(row.transaction_codes);

        this.studentAddress = row.address ?? row.student_address ?? "";
        const transferAreaRaw = row.idp_transfer_area ?? row.idp_transfer_area_id ?? row.idp_transfer_area_code ?? "";
        if (transferAreaRaw && typeof transferAreaRaw === "object") {
            this.idpTransferAreaCode = transferAreaRaw.id ?? transferAreaRaw.code ?? "";
            this.idpTransferArea = transferAreaRaw.name ?? transferAreaRaw.label ?? this.idpTransferAreaCode;
        } else {
            this.idpTransferAreaCode = transferAreaRaw;
            this.idpTransferArea = transferAreaRaw;
        }
        this.idpDueAt = row.idp_due_at ?? row.idp_due_date ?? row.idp_transfer_deadline ?? "";
        this.note = row.note ?? "";

        this.raw = row;
    }

    get showPaymentGuideButton() {
        const method = (this.paymentMethod || "").toString();
        const st = Number(this.status);
        return method === "transfer" && (st === 0 || st === 3);
    }

    /** Hiển thị khi chưa thanh toán đủ (chưa TT / thiếu tiền), giống ManageRetestList */
    get showFixPaymentButton() {
        const st = Number(this.status);
        return st !== 1 && st !== 2;
    }

    /** Số lần có tiền vào (ưu tiên payment_dates, fallback paid_at) */
    get paymentCount() {
        if (this.paymentDates.length) return this.paymentDates.length;
        return this.paidAt ? 1 : 0;
    }

    get hasMultiplePayments() {
        return this.paymentCount > 1;
    }

    /** Cặp ngày + mã GD theo thứ tự API */
    get paymentEntries() {
        const dates = this.paymentDates.map((d) => IdpExamRegisterModel.formatDateTime(d));
        const codes = this.transactionCodes.map((c) => (c ?? "").toString().trim());
        const len = Math.max(dates.length, codes.length);
        if (!len) {
            if (!this.paidAt) return [];
            return [{date: this.paidAt, code: codes[0] || ""}];
        }
        const entries = [];
        for (let i = 0; i < len; i++) {
            entries.push({
                date: dates[i] || "",
                code: codes[i] || "",
            });
        }
        return entries;
    }

    get paidAtDisplay() {
        if (!this.paidAt) return "";
        return IdpExamRegisterModel.formatDisplayDateTime(this.paidAt);
    }

    get idpTransferAreaLabel() {
        const code = (this.idpTransferAreaCode ?? this.idpTransferArea ?? "").toString().trim();
        if (!code) return "";
        const option = IDP_TRANSFER_AREA_OPTIONS.find((o) => o.id === code);
        if (option) return option.name;
        return this.idpTransferArea || code;
    }

    get idpDueAtDisplay() {
        if (!this.idpDueAt) return "";
        const m = moment(this.idpDueAt);
        return m.isValid() ? m.format("DD/MM/YYYY") : this.idpDueAt;
    }

    get paymentDatesExport() {
        return this.paymentEntries
            .map((e) => e.date)
            .filter(Boolean)
            .join("; ");
    }

    get transactionCodesExport() {
        const codes = this.paymentEntries.map((e) => e.code).filter(Boolean);
        return codes.length ? codes.join("; ") : "";
    }

    get paymentStatusLabel() {
        return this.statusLabel;
    }

    get paymentStatusClass() {
        switch (Number(this.status)) {
            case 1:
            case 2:
                return "pay-paid";
            case 3:
                return "pay-shortage";
            default:
                return "pay-unpaid";
        }
    }

    get statusLabel() {
        switch (Number(this.status)) {
            case 1:
                return "Đã thanh toán";
            case 2:
                return "Hoàn thành";
            case 3:
                return "Thiếu tiền";
            default:
                return "Chưa thanh toán";
        }
    }

    get statusClass() {
        switch (Number(this.status)) {
            case 1:
                return "status-paid";
            case 2:
                return "status-done";
            case 3:
                return "status-missing";
            default:
                return "status-unpaid";
        }
    }

    static normalizeStringArray(value) {
        if (value == null) return [];
        if (!Array.isArray(value)) return [];
        return value.filter((item) => item != null && String(item).trim() !== "");
    }

    static formatDateTime(value) {
        if (!value) return "";
        const m = moment(value);
        return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : value;
    }

    static formatDisplayDateTime(value) {
        if (!value) return "";
        const m = moment(value);
        return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : value;
    }
}

export const STATUS_OPTIONS = [
    {
        label: "Chưa thanh toán",
        value: 0,
    },
    {
        label: "Đã thanh toán",
        value: 1,
    },
    {
        label: "Hoàn thành",
        value: 2,
    },
    {
        label: "Thiếu tiền",
        value: 3,
    },
];